/**
 * mention chip 序列化/反序列化工具
 * 用于 contentEditable 输入框中的 @提及 功能跨组件同步
 */

// 新格式：{{ref:category:name:url}}
export const REF_MARKER_RE_V2 = /\{\{ref:([^:}]*):([^:}]*):([^}]+)\}\}/g;
// 旧格式（兼容）：{{ref:name:url}}
export const REF_MARKER_RE = /\{\{ref:([^:}]+):([^}]+)\}\}/g;

const cleanUrlForKind = (url = '') => String(url || '').split('?')[0].split('#')[0].toLowerCase();

export const isVideoReference = (url = '', category = '') => {
  const cat = String(category || '').toLowerCase();
  const clean = cleanUrlForKind(url);
  return cat.includes('video') || /\.(mp4|mov|webm|m4v)$/i.test(clean);
};

export const isAudioReference = (url = '', category = '') => {
  const cat = String(category || '').toLowerCase();
  const clean = cleanUrlForKind(url);
  return cat.includes('audio') || cat.includes('voice') || cat.includes('sound') || /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(clean);
};

/** 遍历 contentEditable DOM，chip → {{ref:category:name:url}}，文本节点 → 原文 */
export const serializeEditor = (el) => {
  if (!el) return '';
  let text = '';
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.dataset?.mention) {
      const cat = node.dataset.category || '';
      const name = (node.dataset.name || '').replace(/:/g, '：'); // 冒号转全角防止破坏格式
      text += `{{ref:${cat}:${name}:${node.dataset.url}}}`;
    } else {
      node.childNodes.forEach(walk);
    }
  };
  el.childNodes.forEach(walk);
  return text.trim();
};

/** 去掉 {{ref:...}} 标记，只保留纯文本（非 GuaVideo 流程用） */
export const stripRefMarkers = (text) => {
  if (!text) return '';
  return text
    .replace(REF_MARKER_RE_V2, '') // 先去新格式
    .replace(REF_MARKER_RE, '')    // 再去旧格式
    .replace(/\s{2,}/g, ' ')
    .trim();
};

/**
 * 将 {{ref:...}} 标记替换为语义文本（GuaVideo ref2v 专用）
 * @param {string} text - 带标记的 prompt
 * @param {string[]} refImages - guaRefImages URL 数组，用于计算图片编号
 * @returns {string} 替换后的 prompt
 */
export const resolveRefMarkers = (text, refImages = []) => {
  if (!text) return '';
  // 先处理新格式 — GuaVideo 识别 @图片N
  let result = text.replace(REF_MARKER_RE_V2, (_, _category, _name, url) => {
    const idx = refImages.indexOf(url);
    if (idx === -1) return ''; // url 不在参考图里，删除
    return `@图片${idx + 1}`;
  });
  // 再处理旧格式（fallback）
  result = result.replace(REF_MARKER_RE, (_, _name, url) => {
    const idx = refImages.indexOf(url);
    if (idx === -1) return '';
    return `@图片${idx + 1}`;
  });
  return result.replace(/\s{2,}/g, ' ').trim();
};

/**
 * GuaVideo 多模态引用：图片/视频/音频分别替换为 @图片N / @视频N / @音色N。
 */
export const resolveGuaVideoRefMarkers = (text, refs = {}) => {
  if (!text) return '';
  const images = refs.images || [];
  const videos = refs.videos || [];
  const audios = refs.audios || [];
  const audioOffset = Number(refs.audioOffset || 0);

  const resolveOne = (category, url) => {
    if (videos.includes(url) || isVideoReference(url, category)) {
      const idx = videos.indexOf(url);
      return idx >= 0 ? `@视频${idx + 1}` : '';
    }
    if (audios.includes(url) || isAudioReference(url, category)) {
      const idx = audios.indexOf(url);
      return idx >= 0 ? `@音色${audioOffset + idx + 1}` : '';
    }
    const idx = images.indexOf(url);
    return idx >= 0 ? `@图片${idx + 1}` : '';
  };

  let result = text.replace(REF_MARKER_RE_V2, (_, category, _name, url) => resolveOne(category, url));
  result = result.replace(REF_MARKER_RE, (_, _name, url) => resolveOne('', url));
  return result.replace(/\s{2,}/g, ' ').trim();
};

/**
 * HappyHorse R2V 专用：将 {{ref:...}} 标记替换为 characterN
 * 按 media 数组顺序，第1张参考图对应 character1，第2张对应 character2...
 * @param {string} text - 带标记的 prompt
 * @param {string[]} refImages - 参考图 URL 数组
 * @returns {string} 替换后的 prompt
 */
export const resolveRefMarkersToCharacter = (text, refImages = []) => {
  if (!text) return '';
  let result = text.replace(REF_MARKER_RE_V2, (_, _category, _name, url) => {
    const idx = refImages.indexOf(url);
    if (idx === -1) return '';
    return `character${idx + 1}`;
  });
  result = result.replace(REF_MARKER_RE, (_, _name, url) => {
    const idx = refImages.indexOf(url);
    if (idx === -1) return '';
    return `character${idx + 1}`;
  });
  return result.replace(/\s{2,}/g, ' ').trim();
};

/** 创建一个 mention chip DOM 元素 */
export const createMentionChip = (name, url, color, category = '') => {
  const chip = document.createElement('span');
  chip.contentEditable = 'false';
  chip.dataset.mention = 'true';
  chip.dataset.url = url;
  chip.dataset.name = name;
  chip.dataset.category = category;
  Object.assign(chip.style, {
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    background: `${color}20`, border: `1px solid ${color}40`,
    borderRadius: '6px', padding: '1px 6px 1px 2px', margin: '0 2px',
    verticalAlign: 'middle', fontSize: '12px', lineHeight: '1.5',
    userSelect: 'all', cursor: 'default',
  });
  const mediaKind = isVideoReference(url, category) ? 'video' : isAudioReference(url, category) ? 'audio' : 'image';
  const media = mediaKind === 'image' ? document.createElement('img') : document.createElement('span');
  if (mediaKind === 'image') {
    media.src = url;
  } else {
    media.textContent = mediaKind === 'video' ? 'V' : 'A';
  }
  Object.assign(media.style, {
    width: '18px', height: '18px', borderRadius: '4px', objectFit: 'cover', flexShrink: '0',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: `${color}24`, color, fontSize: '10px', fontWeight: '700',
  });
  const label = document.createElement('span');
  label.textContent = name;
  label.style.color = color;
  chip.appendChild(media);
  chip.appendChild(label);
  return chip;
};

/** 创建一个小尺寸的 mention chip（用于紧凑布局如 ShotVisuals） */
export const createMentionChipSmall = (name, url, color, category = '') => {
  const chip = document.createElement('span');
  chip.contentEditable = 'false';
  chip.dataset.mention = 'true';
  chip.dataset.url = url;
  chip.dataset.name = name;
  chip.dataset.category = category;
  Object.assign(chip.style, {
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    background: `${color}20`, border: `1px solid ${color}40`,
    borderRadius: '5px', padding: '0px 5px 0px 1px', margin: '0 1px',
    verticalAlign: 'middle', fontSize: '10px', lineHeight: '1.4',
    userSelect: 'all', cursor: 'default',
  });
  const mediaKind = isVideoReference(url, category) ? 'video' : isAudioReference(url, category) ? 'audio' : 'image';
  const media = mediaKind === 'image' ? document.createElement('img') : document.createElement('span');
  if (mediaKind === 'image') {
    media.src = url;
  } else {
    media.textContent = mediaKind === 'video' ? 'V' : 'A';
  }
  Object.assign(media.style, {
    width: '14px', height: '14px', borderRadius: '3px', objectFit: 'cover', flexShrink: '0',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: `${color}24`, color, fontSize: '8px', fontWeight: '700',
  });
  const label = document.createElement('span');
  label.textContent = name;
  label.style.color = color;
  chip.appendChild(media);
  chip.appendChild(label);
  return chip;
};

/** 解析带 {{ref:...}} 标记的文本，还原为 DOM（文本 + chip 混排） */
export const renderPromptToEditor = (el, text, color, small = false) => {
  if (!el) return;
  el.innerHTML = '';
  if (!text) return;

  // 统一正则：优先匹配新 3 段格式，fallback 旧 2 段格式
  const combinedRe = /\{\{ref:([^:}]*):([^:}]*):([^}]+)\}\}|\{\{ref:([^:}]+):([^}]+)\}\}/g;
  let lastIndex = 0;
  let match;
  while ((match = combinedRe.exec(text)) !== null) {
    if (match.index > lastIndex) {
      el.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
    }
    let category, name, url;
    if (match[1] !== undefined) {
      // 新格式：match[1]=category, match[2]=name, match[3]=url
      category = match[1];
      name = (match[2] || '').replace(/：/g, ':');
      url = match[3];
    } else {
      // 旧格式：match[4]=name, match[5]=url
      category = '';
      name = match[4];
      url = match[5];
    }
    const chip = small
      ? createMentionChipSmall(name, url, color, category)
      : createMentionChip(name, url, color, category);
    el.appendChild(chip);
    lastIndex = combinedRe.lastIndex;
  }
  if (lastIndex < text.length) {
    el.appendChild(document.createTextNode(text.substring(lastIndex)));
  }
};
